import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../axiosInstance';
import { toast } from '../../../../utils/toast';
import styles from './WorkflowViewer.module.css';
import Grid from 'apollo-react/components/Grid';
import Paper from 'apollo-react/components/Paper';
import Typography from '../../../common/StyledTypography';
import Button from 'apollo-react/components/Button';
import Divider from 'apollo-react/components/Divider';
import CircularProgress from 'apollo-react/components/CircularProgress';
import WorkflowBuilder from './WorkflowBuilder';
import { WorkflowProvider } from './WorkflowContext';
import { convertUTCToLocal } from '../../../utils/dateUtils';

const WorkflowViewer = () => {
  const { vId: versionId } = useParams();
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const workflowBuilderRef = useRef(null);
  const imageToastShownRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [workflowVersion, setWorkflowVersion] = useState(null);
  const [preJsonData, setPreJsonData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  // Parse preJsonData for WorkflowProvider initialization
  const parsedWorkflow = useMemo(() => {
    if (!preJsonData) return null;
    try {
      return typeof preJsonData === 'string'
        ? JSON.parse(preJsonData)
        : preJsonData;
    } catch {
      return null;
    }
  }, [preJsonData]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    const fetchWorkflowVersion = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows/version/${versionId}`,
          { withCredentials: true }
        );

        if (response.data && response.data.data) {
          const versionData = response.data.data;
          setWorkflowVersion(versionData);
          setPreJsonData(versionData.stages_json);

          if (versionData.workflow_image_id) {
            try {
              const imageResponse = await axiosInstance.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/files/admin/${versionData.workflow_image_id}/preview`,
                {
                  withCredentials: true,
                  responseType: 'blob',
                }
              );

              if (imageResponse.status !== 200 || !imageResponse.data?.size) {
                if (!imageToastShownRef.current) {
                  toast.error(t('Workflow image is not available.'), {
                    position: 'top-center',
                  });
                  imageToastShownRef.current = true;
                }
                return;
              }

              const blob = new Blob([imageResponse.data], {
                type: imageResponse.headers['content-type'] || 'image/jpeg',
              });
              const url = window.URL.createObjectURL(blob);
              setImageUrl(url);
            } catch (imageError) {
              console.warn('Could not load workflow image:', imageError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching workflow version:', error);
        toast.error(t('Error loading workflow version'), {
          position: 'top-center',
        });
      } finally {
        setLoading(false);
      }
    };

    if (versionId) {
      fetchWorkflowVersion();
    }
  }, [versionId, t]);

  if (!ready || loading) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div className={styles.loadingContainer}>
            <CircularProgress />
            <Typography variant="bodySmall" className={styles.marginTop16}>
              {t('Loading...')}
            </Typography>
          </div>
        </Grid>
      </Grid>
    );
  }

  if (!workflowVersion) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Paper className={styles.paper}>
            <Typography variant="bodySmall" className={styles.errorText}>
              {t('Workflow not found.')}
            </Typography>
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className={`${styles.button} ${styles.marginTop16}`}
            >
              {t('Back')}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      <Grid size={12}>
        <Paper className={styles.paper}>
          <div className={styles.sectionSpacing}>
            <Typography variant="h6" className={styles.sectionTitle}>
              {t('Workflow Information')}
            </Typography>

            <Divider />

            <Grid
              container
              spacing={3}
              sx={{ width: '100%' }}
              className={styles.infoGrid}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Name')}
                </Typography>
                <Typography variant="bodyDefault" className={styles.infoValue}>
                  {workflowVersion.name}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Version')}
                </Typography>
                <Typography variant="bodyDefault" className={styles.infoValue}>
                  V{workflowVersion.version}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Creation Date')}
                </Typography>
                <Typography variant="bodyDefault" className={styles.infoValue}>
                  {convertUTCToLocal(workflowVersion.createdAt)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Published At')}
                </Typography>
                <Typography variant="bodyDefault" className={styles.infoValue}>
                  {workflowVersion.publishedAt
                    ? convertUTCToLocal(workflowVersion.publishedAt)
                    : t('Not Published')}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Description')}
                </Typography>
                <Typography variant="bodyDefault" className={styles.infoValue}>
                  {workflowVersion.description || t('No Description')}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="bodySmall" className={styles.infoLabel}>
                  {t('Workflow Image')}
                </Typography>
                <div className={styles.imageContainer}>
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={workflowVersion.name}
                      className={styles.workflowImage}
                    />
                  ) : (
                    <div className={styles.noImage}>
                      <Typography variant="bodySmall">
                        {t('No Image Available')}
                      </Typography>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
          <div className={styles.sectionSpacing}>
            <Typography variant="h6" className={styles.sectionTitle}>
              {t('Workflow Builder')}
            </Typography>

            <Divider />

            <div className={styles.workflowContainer}>
              {parsedWorkflow ? (
                <WorkflowProvider initialWorkflow={parsedWorkflow}>
                  <WorkflowBuilder ref={workflowBuilderRef} />
                </WorkflowProvider>
              ) : (
                <div className={styles.noDataContainer}>
                  <Typography variant="bodySmall">
                    {t('No workflow diagram available')}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default WorkflowViewer;
